// User types for authentication and user data

export interface Department {
    id: number;
    name: string;
    active: boolean;
    client_id: number;
}

export interface ClientAccountingCode {
    id: number;
    client_id: number;
    name: string;
    status: boolean;
}

export interface Client {
    id: number;
    client: string;
    phone: string;
    fax: string;
    disable?: boolean;
    active?: boolean;
    approval_required?: boolean;
    number_of_approvals?: number | null;
    enable_accounting_codes?: boolean;
    accounting_codes?: ClientAccountingCode[];
    departments?: Department[];
    portal_name?: string;
    notify?: boolean;
    storage_fee_tier1?: number;
    email_contacts?: string;
    product_access?: boolean;
    menu_options?: string[];
    fedex_enabled?: boolean;
    fedex_account_number?: string | null;
    fedex_pricing_policy?: string;
    fedex_pricing_policy_arg?: string | null;
    fedex_economy_enabled?: boolean;
    fedex_economy_account_number?: string | null;
    fedex_economy_pricing_policy?: string;
    fedex_economy_pricing_policy_arg?: string | null;
    ups_enabled?: boolean;
    ups_account_number?: string | null;
    ups_billing_postalcode?: string;
    ups_pricing_policy?: string;
    ups_pricing_policy_arg?: string | null;
    ups_economy_enabled?: boolean;
    ups_economy_account_number?: string | null;
    ups_economy_billing_postalcode?: string;
    ups_economy_pricing_policy?: string;
    ups_economy_pricing_policy_arg?: string | null;
    shipping_allow_local_pickup?: boolean;
    shipping_allow_external?: boolean;
    shipping_allow_one_day?: boolean | null;
    shipping_allow_two_day?: boolean | null;
    shipping_allow_ground?: boolean | null;
    shipping_allow_economy?: boolean | null;
    shipping_allow_weekend_delivery?: boolean | null;
}

export interface User {
    id: number;
    email: string;
    alternateUsername?: string;
    fastpass?: string;
    fullName: string;
    phone: string;
    fax: string;
    mobile: string;
    accountingCode: { id: number; name: string } | null;
    departments?: Department[];
    switched: boolean;
    apiToken?: string;
    // Admin-only fields
    client?: Client;
    active?: boolean;
    disable?: boolean;
    admin?: boolean;
    bulkOrder?: boolean;
    locationId?: number;
    privOrder?: boolean;
    privPayAccount?: boolean;
    privPayCard?: boolean;
    rightsAdmin?: boolean;
    rightsReports?: boolean;
    restricted?: boolean;
    shippingAllowLocalPickup?: boolean;
    shippingAllowExternal?: boolean;
    shippingAllowOneDay?: boolean;
    shippingAllowTwoDay?: boolean;
    shippingAllowGround?: boolean;
    shippingAllowEconomy?: boolean;
    shippingAllowWeekendDelivery?: boolean;
    // Vendor-only fields
    clientId?: number;
    privAll?: boolean;
    privEmail?: boolean;
    privUser?: boolean;
    rightsAccount?: boolean;
    rightsHelp?: boolean;
    rightsOrders?: boolean;
    rightsQueue?: boolean;
    rightsWare?: boolean;
    globalAdmin?: boolean;
    distributorAdmin?: boolean;
    clientAdmin?: boolean;
}

export interface UserApiResponse {
    id: number;
    email: string;
    alternateUsername?: string;
    fastpass?: string | null;
    fullName: string;
    phone: string;
    fax: string;
    mobile: string;
    accountingCode: { id: number; name: string } | null;
    departments?: Department[];
    switched: boolean;
    api_token?: string;
    client?: {
        id?: number;
        client?: string;
        phone?: string;
        fax?: string;
        enable_accounting_codes?: boolean;
        accounting_codes?: ClientAccountingCode[];
        [key: string]: unknown;
    };
    active?: boolean;
    disable?: boolean;
    admin?: boolean;
    bulk_order?: boolean;
    location_id?: number;
    priv_order?: boolean;
    priv_pay_account?: boolean;
    priv_pay_card?: boolean;
    rights_admin?: boolean;
    rights_reports?: boolean;
    restricted?: boolean;
    shipping_allow_local_pickup?: boolean;
    shipping_allow_external?: boolean;
    shipping_allow_one_day?: boolean;
    shipping_allow_two_day?: boolean;
    shipping_allow_ground?: boolean;
    shipping_allow_economy?: boolean;
    shipping_allow_weekend_delivery?: boolean;
    client_id?: number;
    priv_all?: boolean;
    priv_email?: boolean;
    priv_user?: boolean;
    rights_account?: boolean;
    rights_help?: boolean;
    rights_orders?: boolean;
    rights_queue?: boolean;
    rights_ware?: boolean;
    global_admin?: boolean;
    distributor_admin?: boolean;
    client_admin?: boolean;
}

export type GiftClaimStatus =
    | 'pending'
    | 'ready_to_share'
    | 'sent'
    | 'failed'
    | 'opened'
    | 'claimed'
    | 'admin_claimed'
    | 'reclaim'
    | 'ordered';

export interface GiftClaim {
    publicId: string;
    programName: string;
    heading: string | null;
    claimUrl: string;
    status: GiftClaimStatus;
}

export function isGiftClaimClaimed(claim: GiftClaim): boolean {
    return claim.status === 'claimed' || claim.status === 'admin_claimed';
}
